import type { RouteRenderMode } from "./resolveRoutePath";
import { strokeOptionsForStyle } from "./routeLineStyle";
import { lineStyleForMode } from "./routeViewModes";

// The comparison lines behind stacking: one plain polyline per trip per
// extra ticked view, each with that view's own geometry and line style.
//
// Deliberately inert - not clickable, so they never compete with the catch
// lines in routeHitArea, and no pins, replay or tooltips. They exist to
// show where two methods disagree, which needs both on screen at once.
//
// Drawn under the primary line, so the line you can interact with always
// reads on top of its own references.
const OVERLAY_Z_INDEX = 0;

// the references sit back from the line that actually carries the trip
const OVERLAY_OPACITY_FACTOR = 0.75;

export type OverlayTrip = { tripKey: string; color: string };

type LatLng = { lat: number; lng: number };

export const createRouteOverlayLines = ({ map }: { map: any }) => {
  const google = (window as any).google;

  // tripKey -> mode -> polyline
  const lines = new Map<string, Map<RouteRenderMode, any>>();
  // the geometry each (trip, mode) has resolved to so far, kept so a mode
  // turned off and on again redraws without waiting on the resolver
  const paths = new Map<string, Map<RouteRenderMode, LatLng[]>>();

  let trips: OverlayTrip[] = [];
  let modes: RouteRenderMode[] = [];
  let colorByTripKey = new Map<string, string>();

  const pathFor = (tripKey: string, mode: RouteRenderMode) =>
    paths.get(tripKey)?.get(mode);

  const removeLine = (tripKey: string, mode: RouteRenderMode) => {
    const byMode = lines.get(tripKey);
    const line = byMode?.get(mode);
    if (!line) return;
    line.setMap(null);
    byMode?.delete(mode);
  };

  const removeAllLines = () => {
    lines.forEach((byMode) => byMode.forEach((line) => line.setMap(null)));
    lines.clear();
  };

  // a line only goes up once its own geometry has arrived - drawing it on
  // the primary view's path first would show two identical lines that then
  // separate, which reads as a glitch rather than a comparison
  const syncLine = (tripKey: string, mode: RouteRenderMode) => {
    const path = pathFor(tripKey, mode);
    if (!path || path.length < 2) {
      removeLine(tripKey, mode);
      return;
    }

    const byMode = lines.get(tripKey) ?? new Map<RouteRenderMode, any>();
    lines.set(tripKey, byMode);

    const strokeOptions = strokeOptionsForStyle(lineStyleForMode(mode));
    const color = colorByTripKey.get(tripKey) ?? "#000000";
    const existing = byMode.get(mode);

    if (existing) {
      existing.setPath(path);
      existing.setMap(map);
      return;
    }

    byMode.set(
      mode,
      new google.maps.Polyline({
        path,
        map,
        clickable: false,
        strokeColor: color,
        strokeOpacity: strokeOptions.strokeOpacity * OVERLAY_OPACITY_FACTOR,
        strokeWeight: strokeOptions.strokeWeight,
        zIndex: OVERLAY_Z_INDEX,
        icons: strokeOptions.icons.map((iconDef: any) => ({
          ...iconDef,
          icon: {
            ...iconDef.icon,
            strokeColor: color,
            strokeOpacity:
              (iconDef.icon.strokeOpacity ?? 1) * OVERLAY_OPACITY_FACTOR,
          },
        })),
      })
    );
  };

  const syncAll = () => {
    // drop lines for modes that are no longer ticked, or trips that are gone
    const liveTripKeys = new Set(trips.map((trip) => trip.tripKey));
    Array.from(lines.keys()).forEach((tripKey) => {
      const byMode = lines.get(tripKey);
      if (!byMode) return;
      Array.from(byMode.keys()).forEach((mode) => {
        if (!liveTripKeys.has(tripKey) || !modes.includes(mode))
          removeLine(tripKey, mode);
      });
      if (byMode.size === 0) lines.delete(tripKey);
    });

    trips.forEach((trip) =>
      modes.forEach((mode) => syncLine(trip.tripKey, mode))
    );
  };

  return {
    // which extra views to draw. An empty list is the normal single-view
    // case and leaves nothing on the map.
    setModes: (nextModes: RouteRenderMode[]) => {
      modes = nextModes;
      syncAll();
    },

    setTrips: (nextTrips: OverlayTrip[]) => {
      trips = nextTrips;
      colorByTripKey = new Map(
        nextTrips.map((trip) => [trip.tripKey, trip.color])
      );
      syncAll();
    },

    // one (trip, view) pair's geometry, as the resolver returns it
    setPath: (tripKey: string, mode: RouteRenderMode, path: LatLng[]) => {
      const byMode = paths.get(tripKey) ?? new Map<RouteRenderMode, LatLng[]>();
      paths.set(tripKey, byMode);
      byMode.set(mode, path);
      if (modes.includes(mode)) syncLine(tripKey, mode);
    },

    clear: () => {
      removeAllLines();
    },

    destroy: () => {
      removeAllLines();
      paths.clear();
      trips = [];
      modes = [];
    },
  };
};

export type RouteOverlayLines = ReturnType<typeof createRouteOverlayLines>;
